import { FC, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'

import { FormControlLabel, IconButton, Popover, Switch, Tooltip } from '@material-ui/core'
import { Settings } from '@material-ui/icons'

import { RootState } from 'src/Store'
import { profileActions } from 'src/actions/ProfileActions'

import ToolBarSlider from '../toolbarslider/ToolBarSlider'
import { ProfileSetting } from 'src/reducers/ProfileReducer'

import { profileSettingsStyle } from './ProfileSettings.style'
import { LeftSideBarIcon, LeftSideBarIconType } from 'src/components/leftsidebar/leftsidebaricon/LeftSideBarIcon'
import { tooltipDelay } from 'src/Constants'

export interface ProfileSettingsProps { } // eslint-disable-line @typescript-eslint/no-empty-interface

const defaultProps: ProfileSettingsProps = {}

export const ProfileSettings: FC<ProfileSettingsProps> = (props: ProfileSettingsProps) => {
  const dispatch = useDispatch()
  const classes = profileSettingsStyle()
  const intl = useIntl()

  const profileMaxDepth = useSelector((state: RootState): number => state.profileState.maxDepth)
  const profileMaxBoreholeDistance = useSelector((state: RootState): number => state.profileState.maxBoreholeDistance)
  const enabledProfileSettings = useSelector((state: RootState): ProfileSetting[] => state.profileState.enabledSettings)
  const [updatingProfile, setUpdatingProfile] = useState<number | undefined>(undefined)

  // Logic for view settings popover
  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)
  const id = open ? 'view-settings' : undefined

  /**
   * Handle click to show the popover.
   * @param event Mouse event.
   */
  const handleClick = (event: any): void => { // TODO: What type should be provided here?
    setAnchorEl(event.currentTarget)
  }

  /**
   * Handle closing the popover.
   */
  const handleClose = (): void => {
    setAnchorEl(null)
  }

  /**
   * Is a given profile setting enabled?
   * @param setting Layer to check.
   * @returns true if layer is selected, false otherwise.
   */
  const isProfileSettingEnabled = (setting: ProfileSetting): boolean => {
    return enabledProfileSettings.some(enabledSetting => enabledSetting === setting)
  }

  /**
   * Refresh profile with a small delay to avoid too many request sent.
   */
  const refresh = (): void => {
    if (updatingProfile === undefined) {
      startUpdateProfileInterval()
    } else {
      clearTimeout(updatingProfile)
      startUpdateProfileInterval()
    }
  }

  /**
   * Set the max borehole distance.
   * @param distance Distance to set.
   * @param refreshProfile Refresh the profile on the map.
   */
  const setMaxBoreholeDistance = (distance: number, refreshProfile: boolean): void => {
    dispatch(profileActions.setMaxBoreholeDistance(distance))
    if (refreshProfile) {
      refresh()
    }
  }

  /**
   * Set the max depth.
   * @param depth Depth to set.
   * @param refreshProfile Refresh the profile on the map.
   */
  const setMaxDepth = (depth: number, refreshProfile: boolean): void => {
    dispatch(profileActions.setMaxDepth(depth))
    if (refreshProfile) {
      refresh()
    }
  }

  /**
   * Start delay before update the profile on the map.
   */
  const startUpdateProfileInterval = (): void => {
    const timeout = window.setTimeout(() => {
      dispatch(profileActions.fetchProfile())
      setUpdatingProfile(undefined)
    }, 500)
    setUpdatingProfile(timeout)
  }

  /**
   * Generate view settings switch.
   * @param setting Setting to generate switch for.
   * @returns Switch element
   */
  const generateProfileSettingSwitch = (setting: ProfileSetting): JSX.Element => {
    const checked = isProfileSettingEnabled(setting)
    const name = intl.formatMessage({ id: `tools.profile.view.${setting as string}` })
    const disabled = setting === ProfileSetting.SHOW_WATER_LEVEL && !isProfileSettingEnabled(ProfileSetting.SHOW_WATER_LEVEL_SYMBOL)
    return (
      <div>
        <FormControlLabel
          control={
            <Switch
              checked={checked}
              name={name}
              color='primary'
              disabled={disabled}
              onChange={event => handleSwitchChange(setting, event.target.checked)}
            />
          }
          label={name}
          className={classes.viewSettingsLabel}
        />
      </div>
    )
  }

  /**
   * Handle when a switch value is changed.
   * @param setting Setting to change.
   * @param checked true if checked, false otherwise.
   */
  const handleSwitchChange = (setting: ProfileSetting, checked: boolean): void => {
    dispatch(checked ? profileActions.enableSetting(setting) : profileActions.disableSetting(setting))
  }

  return (
    <div className={classes.root}>
      <Tooltip
        title={intl.formatMessage({ id: 'tools.profile' })}
        enterDelay={tooltipDelay}
        placement='bottom'
      >
        <div className={classes.toolIcon}>
          <LeftSideBarIcon icon={LeftSideBarIconType.Profile} selected disabled={false} />
        </div>
      </Tooltip>
      <div className={classes.setting}>
        <ToolBarSlider label='tools.profile.depth' name='depth' unit='m' min={-200} max={200} step={10} value={profileMaxDepth} onChangeHandler={setMaxDepth} />
      </div>
      <div className={classes.setting}>
        <ToolBarSlider label='tools.profile.boreholedist' name='boreholeDistance' min={0} max={1000} step={10} unit='m' value={profileMaxBoreholeDistance} onChangeHandler={setMaxBoreholeDistance} />
      </div>
      <div className={classes.spacer} />
      <div>
        <div>
          <IconButton color='primary' className={classes.viewButton} onClick={handleClick}>
            <Settings />
          </IconButton>
          <Popover
            id={id}
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left'
            }}
          >
            <div className={classes.viewSettings}>
              <div className={classes.viewSettingsDescription}>
                <FormattedMessage id='tools.profile.view.description' />
              </div>
              {generateProfileSettingSwitch(ProfileSetting.SHOW_BOREHOLE_LABEL)}
              {generateProfileSettingSwitch(ProfileSetting.SHOW_GEOLOGY_LABEL)}
              {generateProfileSettingSwitch(ProfileSetting.SHOW_FILTER_LEVEL)}
              {generateProfileSettingSwitch(ProfileSetting.SHOW_WATER_LEVEL_SYMBOL)}
              {generateProfileSettingSwitch(ProfileSetting.SHOW_WATER_LEVEL)}
              {generateProfileSettingSwitch(ProfileSetting.SHOW_GEO_LOGO)}
              {generateProfileSettingSwitch(ProfileSetting.SHOW_PROFILE_BUFFER)}
            </div>
          </Popover>
        </div>
      </div>
    </div>
  )
}

ProfileSettings.defaultProps = defaultProps

export default ProfileSettings
